import { Link } from "react-router-dom";
import { motion } from "@/components/motion";
import { ArrowRight, MapPin } from "lucide-react";
import { getLocationBySlug } from "@/data/locations";
import { PRIORITY_CITY_SLUGS } from "@/data/priorityCombos";

/**
 * Homepage entry point into the /suppliers directory and the city landing pages.
 * Gives Google a crawlable internal link from the homepage to every kept city.
 */
/**
 * Local keyword clusters buyers actually search for. Each one points at the
 * page that answers it, so the homepage passes relevance to that page.
 */
const LOCAL_SEARCH_LINKS = [
  { label: "GI pipe price per kg", to: "/price-lists/gi-pipe-price-per-kg" },
  { label: "Steel pipe dealers in Coimbatore", to: "/supplier/ms-round-pipes-in-coimbatore" },
  { label: "GI pipe dealers in Coimbatore", to: "/supplier/gi-pipes-tubes-in-coimbatore" },
  { label: "MS pipe dealers in Chennai", to: "/supplier/ms-round-pipes-in-chennai" },
  { label: "GI pipe dealers in Bangalore", to: "/supplier/gi-pipes-tubes-in-bangalore" },
  { label: "Steel pipe dealers in Kochi", to: "/supplier/ms-round-pipes-in-kochi" },
  { label: "Tata pipe price list", to: "/price-lists/tata-pipes" },
  { label: "MS square pipe price in Coimbatore", to: "/supplier/ms-square-pipes-in-coimbatore" },
  { label: "ERW tube stockist in Chennai", to: "/supplier/erw-steel-tubes-in-chennai" },
  { label: "Zoloto valve price list", to: "/price-lists/zoloto-valves" },
  { label: "Today's steel pipe rate", to: "/steel-price-today" },
  { label: "Coimbatore buyer FAQs", to: "/faq/coimbatore" },
];

const SupplyLocations = () => {
  const cities = PRIORITY_CITY_SLUGS.map((slug) => getLocationBySlug(slug)).filter(Boolean);

  return (
    <section id="locations" className="py-20 md:py-28 bg-secondary">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <p className="text-accent-strong font-display text-sm uppercase tracking-[0.2em] mb-2">
            Where We Deliver
          </p>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground">
            Steel Pipe Supply Locations
          </h2>
          <p className="font-body text-muted-foreground mt-3 max-w-xl mx-auto">
            Daily dispatches from our Coimbatore warehouse. Pick your city for delivery areas,
            stock sizes and today's rate.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
          {cities.map((city, i) => (
            <motion.div
              key={city!.slug}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="bg-card border border-border rounded-lg p-5 flex flex-col"
            >
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4 text-accent shrink-0" />
                <h3 className="font-display text-lg font-bold text-foreground">{city!.state}</h3>
              </div>
              <p className="font-body text-sm text-muted-foreground mb-4 flex-1">
                {city!.cities.slice(0, 4).join(", ")}
              </p>
              <Link
                to={`/locations/${city!.slug}`}
                className="font-display text-sm font-semibold text-accent inline-flex items-center gap-1.5"
              >
                {city!.state} supply details <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="max-w-5xl mx-auto mt-12">
          <h3 className="font-display text-base font-bold text-foreground text-center mb-1">
            Popular searches we answer
          </h3>
          <p className="font-body text-sm text-muted-foreground text-center mb-5">
            Steel pipe dealer rates, price per kg and stock — city by city.
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {LOCAL_SEARCH_LINKS.map((l) => (
              <Link
                key={l.to + l.label}
                to={l.to}
                className="rounded-full bg-card border border-border px-4 py-2 font-body text-sm text-foreground hover:border-accent transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="text-center mt-10 flex flex-wrap justify-center gap-3">
          <Link
            to="/suppliers"
            className="inline-flex items-center gap-2 bg-accent text-accent-foreground font-display font-semibold text-sm px-6 py-3 rounded hover:brightness-110 transition"
          >
            View all supply locations &amp; stock sizes <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/delivery-map"
            className="inline-flex items-center gap-2 border border-border bg-card font-display font-semibold text-sm px-6 py-3 rounded hover:border-accent transition text-foreground"
          >
            <MapPin className="w-4 h-4 text-accent" /> Delivery map
          </Link>
          <Link
            to="/branches"
            className="inline-flex items-center gap-2 border border-border bg-card font-display font-semibold text-sm px-6 py-3 rounded hover:border-accent transition text-foreground"
          >
            <MapPin className="w-4 h-4 text-accent" /> Branch address, phone &amp; hours
          </Link>
        </div>
      </div>
    </section>
  );
};

export default SupplyLocations;
